/**
 * Management tab (form key Prescription) – Medication Details, Lab Tests & Investigation,
 * Patient's Instruction; patient card; light blue section borders; Cancel / Save Prescription.
 * Bound to FormDataContext so DB-stored and LLM-extracted prescription fields render and save.
 */
import { useState, useMemo, useEffect } from 'react';
import {
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Box,
  FormControlLabel,
  Switch,
  Autocomplete,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Description as DescriptionIcon,
  Science as ScienceIcon,
  Person as PersonIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  UploadFile as UploadFileIcon,
} from '@mui/icons-material';
import RightPanelTabs from '../RightPanel/RightPanelTabs';
import { RIGHT_PANEL_WIDTH, RIGHT_PANEL_WIDTH_XS } from '../RightPanel/constants';
import AmbientMicButton from '../AmbientMicButton/AmbientMicButton';
import FormField from '../Common/FormField';
import { useConfidenceToggle } from '../../contexts/ConfidenceToggleContext';
import { useUser } from '../../contexts/UserContext';
import { useSession } from '../../contexts/SessionContext';
import { useFormData } from '../../contexts/FormDataContext';
import WorkflowSessionBar from '../Workflow/WorkflowSessionBar';
import { apiFetch } from '../../api/client';
import PrescriptionUpload from '../PrescriptionUpload/PrescriptionUpload';
import type { PrescriptionUploadMedication } from '../../services/api';

const TAB_NAME = 'Prescription';
const ENABLE_PRESCRIPTION_UPLOAD = (import.meta.env.VITE_ENABLE_PRESCRIPTION_UPLOAD ?? 'true') !== 'false';

/* Same as Patient Visit: section and form field border #8DBAEA */
const SECTION_BORDER = '#8DBAEA';
const INPUT_SX = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#FFFFFF',
    borderRadius: '6px',
    '& fieldset': { borderColor: '#8DBAEA', borderWidth: '1px' },
    '&:hover fieldset': { borderColor: '#7aa8e0' },
    '&.Mui-focused fieldset': { borderColor: '#012676', borderWidth: 1 },
  },
  '& .MuiInputLabel-root': { color: '#333333', fontFamily: 'Arial, Helvetica, sans-serif' },
  '& .MuiOutlinedInput-input::placeholder': { color: '#9E9E9E' },
};

const SECTION_PAPER_SX = {
  borderRadius: '6px',
  border: '1px solid #8DBAEA',
  borderColor: '#8DBAEA',
  p: 2.5,
  mb: 2.5,
  bgcolor: '#FFFFFF',
};

/* Lab test option with optional confidence for display */
const LAB_TEST_OPTIONS = [
  'LDL Cholesterol (mg/dl)',
  'TSH',
  'Thyroid peroxidase antibody (IU/ml)',
  'HbA1c',
  'Fasting Blood Glucose',
  'Complete Blood Count (CBC)',
  'Serum Creatinine',
  'Urine Microalbumin',
  'HbA1C test',
  'fasting glucose test',
  'liver function test',
];

interface LabTestTag {
  label: string;
  confidence?: number;
}

interface MedicationItem {
  /** FK to `medication_master.medication_id` when matched to formulary */
  master_medication_id?: number;
  drug_name?: string;
  dose?: string;
  unit?: string;
  frequency?: string;
  route?: string;
  duration?: string;
  plan_change?: string;
  reason_for_change?: string;
  medication_instructions?: string;
}

interface MedCatalogOption {
  medication_id: number;
  label: string;
  generic_name: string;
  brand_name?: string | null;
  strength?: string | null;
  unit?: string | null;
  dosage_form?: string | null;
  route_of_administration?: string | null;
  rank: number;
}

export default function Prescription() {
  const { showConfidence, setShowConfidence } = useConfidenceToggle();
  const { patient } = useUser();
  const { session } = useSession();
  const { getFieldValue, updateField, saveFormData, clearFormForTab } = useFormData();

  const getFieldString = (fieldName: string): string => {
    const raw = getFieldValue(TAB_NAME, fieldName);
    return raw == null ? '' : String(raw);
  };

  const setFieldString = (fieldName: string, value: string) => {
    updateField(TAB_NAME, fieldName, value);
  };

  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'success' | 'error'>('idle');
  const [labTestInput, setLabTestInput] = useState('');
  const [catalogOptions, setCatalogOptions] = useState<MedCatalogOption[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  /** When user picks a formulary row, we stamp `master_medication_id` on the next save/add. */
  const [pendingMasterMedicationId, setPendingMasterMedicationId] = useState<number | null>(null);
  const [snackbar, setSnackbar] = useState<string | null>(null);
  const [isUploadOpen, setIsUploadOpen] = useState(false);

  // Medications list: derived from form context so it updates for both manual add and Ambient AI
  const medicationsFromContext = getFieldValue(TAB_NAME, 'medications_prescribed');
  const medications = useMemo((): MedicationItem[] => {
    const raw = medicationsFromContext;
    if (raw == null || !Array.isArray(raw)) return [];
    return raw as MedicationItem[];
  }, [medicationsFromContext]);

  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const drugNameInput = getFieldString('drug_name');

  useEffect(() => {
    const q = drugNameInput.trim();
    if (q.length < 2) {
      setCatalogOptions([]);
      return;
    }
    const t = window.setTimeout(() => {
      setCatalogLoading(true);
      void apiFetch(`/api/medications/search?q=${encodeURIComponent(q)}&limit=20`)
        .then((r) => (r.ok ? r.json() : []))
        .then((rows: MedCatalogOption[]) => {
          setCatalogOptions(Array.isArray(rows) ? rows : []);
        })
        .catch(() => setCatalogOptions([]))
        .finally(() => setCatalogLoading(false));
    }, 300);
    return () => window.clearTimeout(t);
  }, [drugNameInput]);

  const labTestsValue = getFieldValue(TAB_NAME, 'lab_tests_advised');
  const labTestTags = useMemo((): LabTestTag[] => {
    const raw = labTestsValue;
    if (raw == null || raw === '') return [];
    if (Array.isArray(raw)) return raw.map((x: string) => ({ label: String(x) }));
    const s = String(raw).trim();
    if (!s) return [];
    return s.split(',').map((x) => ({ label: x.trim() })).filter((x) => x.label);
  }, [labTestsValue]);

  const handleAddLabTest = (option: string | null) => {
    if (!option || labTestTags.some((t) => t.label === option)) return;
    const next = [...labTestTags, { label: option }];
    updateField(TAB_NAME, 'lab_tests_advised', next.map((t) => t.label).join(', '));
    setLabTestInput('');
  };

  const handleRemoveLabTest = (label: string) => {
    const next = labTestTags.filter((t) => t.label !== label);
    updateField(TAB_NAME, 'lab_tests_advised', next.length ? next.map((t) => t.label).join(', ') : '');
  };

  const handleCancel = () => {
    clearFormForTab(TAB_NAME);
    setSaveStatus('idle');
    setEditingIndex(null);
    setPendingMasterMedicationId(null);
  };

  const syncMedicationsToForm = (nextMeds: MedicationItem[]) => {
    updateField(TAB_NAME, 'medications_prescribed', nextMeds);
  };

  const clearMedicationFields = () => {
    for (const name of [
      'drug_name',
      'dose',
      'unit',
      'frequency',
      'route',
      'duration',
      'plan_change',
      'reason_for_change',
      'medication_instructions',
    ]) {
      updateField(TAB_NAME, name, '');
    }
    setPendingMasterMedicationId(null);
  };

  const normDrugKey = (s: string) => s.toLowerCase().trim().replace(/\s+/g, ' ');

  const buildMedicationFromFields = (): MedicationItem | null => {
    const med: MedicationItem = {
      master_medication_id: pendingMasterMedicationId ?? undefined,
      drug_name: getFieldString('drug_name'),
      dose: getFieldString('dose'),
      unit: getFieldString('unit'),
      frequency: getFieldString('frequency'),
      route: getFieldString('route'),
      duration: getFieldString('duration'),
      plan_change: getFieldString('plan_change'),
      reason_for_change: getFieldString('reason_for_change'),
      medication_instructions: getFieldString('medication_instructions'),
    };
    const hasCore =
      (med.drug_name && med.drug_name.trim()) ||
      (med.dose && med.dose.trim()) ||
      (med.frequency && med.frequency.trim());
    return hasCore ? med : null;
  };

  const handleAddOrUpdateMedication = () => {
    const med = buildMedicationFromFields();
    if (!med) {
      return;
    }

    const findDuplicateIndex = (): number => {
      if (med.master_medication_id != null) {
        const byId = medications.findIndex((m) => m.master_medication_id === med.master_medication_id);
        if (byId >= 0) return byId;
      }
      const n = normDrugKey(med.drug_name || '');
      if (n.length < 2) return -1;
      return medications.findIndex((m) => normDrugKey(m.drug_name || '') === n);
    };

    let targetIndex = editingIndex;
    if (targetIndex == null) {
      const dup = findDuplicateIndex();
      if (dup >= 0) targetIndex = dup;
    }

    if (targetIndex != null && targetIndex >= 0 && targetIndex < medications.length) {
      const next = medications.slice();
      next[targetIndex] = med;
      syncMedicationsToForm(next);
      if (editingIndex == null || editingIndex !== targetIndex) {
        setSnackbar('Updated existing medication line (same drug / formulary id).');
      }
    } else {
      syncMedicationsToForm([...medications, med]);
    }
    clearMedicationFields();
    setEditingIndex(null);
  };

  const handleEditMedication = (index: number) => {
    const med = medications[index];
    setEditingIndex(index);
    setPendingMasterMedicationId(med.master_medication_id ?? null);
    setFieldString('drug_name', med.drug_name || '');
    setFieldString('dose', med.dose || '');
    setFieldString('unit', med.unit || '');
    setFieldString('frequency', med.frequency || '');
    setFieldString('route', med.route || '');
    setFieldString('duration', med.duration || '');
    setFieldString('plan_change', med.plan_change || '');
    setFieldString('reason_for_change', med.reason_for_change || '');
    setFieldString('medication_instructions', med.medication_instructions || '');
  };

  const handleDeleteMedication = (index: number) => {
    const next = medications.filter((_, i) => i !== index);
    syncMedicationsToForm(next);
    if (editingIndex === index) {
      clearMedicationFields();
      setEditingIndex(null);
    } else if (editingIndex != null && editingIndex > index) {
      setEditingIndex(editingIndex - 1);
    }
  };

  const handlePrescriptionUploadApproved = (uploadedRows: PrescriptionUploadMedication[]) => {
    const normalized: MedicationItem[] = uploadedRows
      .map((row) => ({
        drug_name: (row.drug_name || '').trim(),
        dose: row.dose ?? undefined,
        unit: row.unit ?? undefined,
        frequency: row.frequency ?? undefined,
        route: row.route ?? undefined,
        duration: row.duration ?? undefined,
        medication_instructions: row.medication_instructions ?? undefined,
      }))
      .filter((row) => row.drug_name);
    if (!normalized.length) return;

    const byDrug = new Map<string, MedicationItem>();
    for (const existing of medications) {
      const key = normDrugKey(existing.drug_name || '');
      if (!key) continue;
      byDrug.set(key, existing);
    }
    for (const incoming of normalized) {
      const key = normDrugKey(incoming.drug_name || '');
      if (!key) continue;
      byDrug.set(key, incoming);
    }
    const next = Array.from(byDrug.values());
    syncMedicationsToForm(next);
    setSnackbar('Prescription upload approved and merged into medication list.');
  };

  const handleSavePrescription = async () => {
    setSaveStatus('saving');
    const result = await saveFormData();
    if (result.success) {
      setSaveStatus('success');
    } else {
      setSaveStatus('error');
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        gap: 2,
        p: 2,
        height: '100%',
        minHeight: 0,
        overflow: 'hidden',
        bgcolor: '#FFFFFF',
        fontFamily: 'Arial, Helvetica, sans-serif',
      }}
    >
      <Box sx={{ flex: { xs: '1 1 0%', md: '1 1 65%' }, minWidth: 0, overflow: 'auto', bgcolor: '#FFFFFF' }}>
        <Box sx={{ flexShrink: 0, mb: 2 }}>
          <WorkflowSessionBar />
        </Box>
        {/* Header: title, subtitle, AI Confidence, Patient card */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#1A4D9A', fontFamily: 'inherit' }}>
              Management
            </Typography>
            <Typography variant="body2" sx={{ color: '#757575', mt: 0.5, fontFamily: 'inherit' }}>
              Complete clinical assessment and documentation
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            {/* AI Confidence Toggle – same look as Registration */}
            <Box sx={{ bgcolor: '#eef3fb', borderRadius: 20, px: 1.5, py: 0.2 }}>
              <FormControlLabel
                control={
                <Switch
                  size="small"
                  checked={showConfidence}
                  onChange={(_, v) => setShowConfidence(v)}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': { color: '#008AD2' },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#008AD2' },
                  }}
                />
              }
                label="AI Confidence"
                sx={{ m: 0, '& .MuiFormControlLabel-label': { fontSize: '0.85rem', color: '#4a4a4a', fontFamily: 'inherit' } }}
              />
            </Box>
            <Paper
              variant="outlined"
              sx={{
                borderRadius: '6px',
                border: '1px solid #8DBAEA',
                borderColor: '#8DBAEA',
                px: 2,
                py: 1.25,
                bgcolor: '#FFFFFF',
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
              }}
            >
              <PersonIcon sx={{ color: '#1A4D9A', fontSize: 22 }} />
              <Box>
                <Typography variant="caption" sx={{ color: '#666666', display: 'block', fontFamily: 'inherit' }}>
                  Patient
                </Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#333333', fontFamily: 'inherit' }}>
                  {patient?.name || 'No patient selected'}
                </Typography>
                <Typography variant="caption" sx={{ color: '#757575', fontFamily: 'inherit' }}>
                  MRN: {patient?.mrn || '–'}
                </Typography>
              </Box>
            </Paper>
          </Box>
        </Box>

        {/* Section 1: Medication Details – heading with full-width light blue line underneath */}
        <Paper sx={SECTION_PAPER_SX}>
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DescriptionIcon sx={{ color: '#1A4D9A', fontSize: 22 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#333333', fontFamily: 'inherit' }}>
                Medication Details
              </Typography>
              </Box>
              {ENABLE_PRESCRIPTION_UPLOAD && (
                <Button
                  variant="outlined"
                  startIcon={<UploadFileIcon />}
                  onClick={() => setIsUploadOpen(true)}
                  sx={{ textTransform: 'none' }}
                >
                  Upload Prescription
                </Button>
              )}
            </Box>
            <Box sx={{ width: '100%', borderBottom: '1px solid #8DBAEA', mt: 1 }} />
          </Box>
          <Grid container spacing={2} sx={{ fontFamily: 'inherit' }}>
            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                freeSolo
                filterOptions={(opts) => opts}
                options={catalogOptions}
                loading={catalogLoading}
                getOptionLabel={(o) => (typeof o === 'string' ? o : o.label)}
                inputValue={drugNameInput}
                onInputChange={(_, v, reason) => {
                  if (reason === 'input' || reason === 'clear') {
                    updateField(TAB_NAME, 'drug_name', v);
                    setPendingMasterMedicationId(null);
                  }
                }}
                onChange={(_, v) => {
                  if (v && typeof v === 'object' && 'medication_id' in v) {
                    updateField(TAB_NAME, 'drug_name', v.label);
                    setPendingMasterMedicationId(v.medication_id);
                  } else if (typeof v === 'string') {
                    updateField(TAB_NAME, 'drug_name', v);
                    setPendingMasterMedicationId(null);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Drug Name"
                    placeholder="Search formulary or type name"
                    size="small"
                    sx={INPUT_SX}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {catalogLoading ? <CircularProgress color="inherit" size={18} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormField tabName={TAB_NAME} fieldName="dose" fullWidth size="small" label="# Dose" placeholder="Enter dose" sx={INPUT_SX} />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormField tabName={TAB_NAME} fieldName="unit" fullWidth size="small" label="Unit" sx={INPUT_SX} />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormField tabName={TAB_NAME} fieldName="frequency" fullWidth size="small" label="Frequency" sx={INPUT_SX} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormField tabName={TAB_NAME} fieldName="route" fullWidth size="small" label="Route" sx={INPUT_SX} />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormField tabName={TAB_NAME} fieldName="duration" fullWidth size="small" label="# Duration" placeholder="Enter duration" sx={INPUT_SX} />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormField tabName={TAB_NAME} fieldName="plan_change" fullWidth size="small" label="Plan Change" sx={INPUT_SX} />
            </Grid>
            <Grid item xs={12}>
              <FormField
                tabName={TAB_NAME}
                fieldName="reason_for_change"
                fullWidth
                size="small"
                label="Reason for change (if modified)"
                placeholder="Enter reason for medication change"
                multiline
                rows={2}
                sx={INPUT_SX}
              />
            </Grid>
            <Grid item xs={12}>
              <FormField
                tabName={TAB_NAME}
                fieldName="medication_instructions"
                fullWidth
                size="small"
                label="Medication instructions"
                placeholder="Enter medication instructions"
                multiline
                rows={2}
                sx={INPUT_SX}
              />
            </Grid>
            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
              <Button
                variant="contained"
                onClick={handleAddOrUpdateMedication}
                sx={{
                  bgcolor: '#1A4D9A',
                  color: '#FFFFFF',
                  borderRadius: '6px',
                  textTransform: 'none',
                  fontFamily: 'inherit',
                  '&:hover': { bgcolor: '#153d7a' },
                }}
              >
                {editingIndex != null ? 'Update Medication' : 'Add Medication'}
              </Button>
            </Grid>
            {medications.length > 0 && (
              <Grid item xs={12}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, color: '#333333', mb: 1, fontFamily: 'inherit' }}
                >
                  Medication Added
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '6px' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Drug Name</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Dose</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Unit</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Frequency</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Route</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Duration</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {medications.map((m, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{m.drug_name || '-'}</TableCell>
                          <TableCell>{m.dose || '-'}</TableCell>
                          <TableCell>{m.unit || '-'}</TableCell>
                          <TableCell>{m.frequency || '-'}</TableCell>
                          <TableCell>{m.route || '-'}</TableCell>
                          <TableCell>{m.duration || '-'}</TableCell>
                          <TableCell align="right">
                            <IconButton size="small" onClick={() => handleEditMedication(idx)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={() => handleDeleteMedication(idx)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            )}
          </Grid>
        </Paper>

        {/* Section 2: Lab Tests & Investigation – heading with full-width light blue line underneath */}
        <Paper sx={SECTION_PAPER_SX}>
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ScienceIcon sx={{ color: '#1A4D9A', fontSize: 22 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#333333', fontFamily: 'inherit' }}>
                Lab Tests & Investigation
              </Typography>
            </Box>
            <Box sx={{ width: '100%', borderBottom: '1px solid #8DBAEA', mt: 1 }} />
          </Box>
          <Grid container spacing={2} sx={{ fontFamily: 'inherit' }}>
            <Grid item xs={12}>
              <Typography variant="body2" sx={{ fontWeight: 500, color: '#333333', mb: 1, fontFamily: 'inherit' }}>Advice lab tests & investigations</Typography>
              <Autocomplete
                freeSolo
                options={LAB_TEST_OPTIONS}
                inputValue={labTestInput}
                onInputChange={(_, v) => setLabTestInput(v)}
                onChange={(_, v) => handleAddLabTest(typeof v === 'string' ? v : v ?? null)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && labTestInput.trim()) {
                    const match = LAB_TEST_OPTIONS.find((o) => o.toLowerCase().includes(labTestInput.toLowerCase()));
                    handleAddLabTest(match || labTestInput.trim());
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    placeholder="Select lab tests..."
                    sx={INPUT_SX}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
              {labTestTags.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
                  {labTestTags.map((t) => (
                    <Chip
                      key={t.label}
                      label={t.label}
                      onDelete={() => handleRemoveLabTest(t.label)}
                      deleteIcon={<CloseIcon sx={{ fontSize: 16 }} />}
                      size="small"
                      sx={{
                        bgcolor: '#ADD8E6',
                        color: '#1A4D9A',
                        fontWeight: 500,
                        borderRadius: '6px',
                        fontFamily: 'inherit',
                        '& .MuiChip-label': { fontSize: '0.8rem' },
                        '& .MuiChip-deleteIcon': { color: '#1A4D9A' },
                      }}
                    />
                  ))}
                </Box>
              )}
            </Grid>
            <Grid item xs={12}>
              <FormField
                tabName={TAB_NAME}
                fieldName="other_lab_investigations"
                fullWidth
                size="small"
                label="Other Lab / Investigations"
                placeholder="Describe any other lab tests or investigations"
                multiline
                rows={3}
                sx={INPUT_SX}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Section 3: Patient's Instruction – heading with full-width light blue line underneath */}
        <Paper sx={SECTION_PAPER_SX}>
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonIcon sx={{ color: '#1A4D9A', fontSize: 22 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#333333', fontFamily: 'inherit' }}>
                Patient&apos;s Instruction
              </Typography>
            </Box>
            <Box sx={{ width: '100%', borderBottom: '1px solid #8DBAEA', mt: 1 }} />
          </Box>
          <Grid container spacing={2} sx={{ fontFamily: 'inherit' }}>
            <Grid item xs={12}>
              <FormField
                tabName={TAB_NAME}
                fieldName="diet_summary"
                fullWidth
                size="small"
                label="Diet Summary"
                placeholder="Enter diet summary and recommendations"
                multiline
                rows={3}
                sx={INPUT_SX}
              />
            </Grid>
            <Grid item xs={12}>
              <FormField
                tabName={TAB_NAME}
                fieldName="lifestyle_plan_summary"
                fullWidth
                size="small"
                label="Lifestyle Plan Summary"
                placeholder="Enter lifestyle plan and recommendations"
                multiline
                rows={3}
                sx={INPUT_SX}
              />
            </Grid>
            <Grid item xs={12}>
              <FormField
                tabName={TAB_NAME}
                fieldName="clinical_instructions"
                fullWidth
                size="small"
                label="Clinical Instructions & Follow-up"
                placeholder="e.g. Monitor BP daily; Seek care if BP exceeds 160/100; Follow up after 2 weeks with reports"
                multiline
                rows={3}
                sx={INPUT_SX}
              />
            </Grid>
            <Grid item xs={12}>
              <FormField
                tabName={TAB_NAME}
                fieldName="next_appointment_date"
                fullWidth
                size="small"
                label="Next appointment date"
                type="date"
                InputLabelProps={{ shrink: true }}
                sx={INPUT_SX}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Action buttons: bottom right of form (same as Pre-Consultation) – Cancel left, Save Prescription right */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, flexWrap: 'wrap', mt: 3, mb: 2 }}>
          <Button
            variant="outlined"
            startIcon={<CloseIcon />}
            onClick={handleCancel}
            sx={{
              borderColor: '#D3D3D3',
              color: '#333333',
              bgcolor: '#FFFFFF',
              borderRadius: '6px',
              borderWidth: '1px',
              fontFamily: 'inherit',
              '&:hover': { borderColor: '#bdbdbd', bgcolor: '#FAFAFA' },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSavePrescription}
            disabled={!patient || saveStatus === 'saving'}
            sx={{
              bgcolor: '#1A4D9A',
              color: '#FFFFFF',
              borderRadius: '6px',
              fontFamily: 'inherit',
              '&:hover': { bgcolor: '#153d7a' },
            }}
          >
            {saveStatus === 'saving' ? 'Saving...' : 'Save Prescription'}
          </Button>
        </Box>

        <AmbientMicButton placement="fixed" />
        <Snackbar
          open={snackbar != null}
          autoHideDuration={5000}
          onClose={() => setSnackbar(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={() => setSnackbar(null)} severity="info" variant="filled" sx={{ width: '100%' }}>
            {snackbar}
          </Alert>
        </Snackbar>
      </Box>

      {/* <Box sx={{ width: { xs: RIGHT_PANEL_WIDTH_XS, md: RIGHT_PANEL_WIDTH }, flexShrink: 0, minHeight: 0, display: 'flex' }}> */}

      <Box
        sx={{
          width: { xs: RIGHT_PANEL_WIDTH_XS, md: RIGHT_PANEL_WIDTH },
          flexShrink: 0,
          flex: { xs: '1 1 0%', md: '0 0 auto' },
          minHeight: 0,
          height: '100%',
          display: 'flex',
          overflow: 'hidden',
        }}
      >
        <RightPanelTabs
        defaultTab="summary"
        sessionId={session?.session_id ?? null}
        showCurrentVisitContent={!!session && session.status === 'active'}
      />
      </Box>
      <PrescriptionUpload
        open={isUploadOpen}
        sessionId={session?.session_id ?? null}
        onClose={() => setIsUploadOpen(false)}
        onApproved={handlePrescriptionUploadApproved}
      />
    </Box>
  );
}
